import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowRight } from "lucide-react";
import { CONTACT, IMAGES } from "../data";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 1.05, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

const HeadLine = ({ children, i, accent }) => (
  <span className="block overflow-hidden py-[0.5vw]">
    <motion.span
      custom={i}
      variants={line}
      initial="hidden"
      animate="show"
      className={`block ${accent ? "chrome-text" : "chrome-text-soft"}`}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = ({ onQuote }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.9]);

  return (
    <section id="top" ref={ref} data-testid="hero" className="relative min-h-[100svh] w-full overflow-hidden">
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 -z-10">
        <img src={IMAGES.hero} alt="FTC workshop engine bay" className="h-full w-full object-cover" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 -z-10 bg-black" />
      <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black via-black/40 to-black/70" />
      {/* light streak */}
      <div className="absolute left-1/2 top-0 -z-10 h-full w-[2px] -translate-x-1/2 bg-gradient-to-b from-transparent via-white/15 to-transparent" />

      <div className="mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-center px-5 pb-24 pt-40 md:px-10">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="mb-8 flex items-center gap-3"
        >
          <span className="flare relative pl-4 font-accent text-[11px] uppercase tracking-[0.4em] text-neutral-400">
            Bristol · Independent Automotive Specialist
          </span>
        </motion.div>

        <h1 className="font-display text-[10.5vw] font-700 uppercase leading-[0.95] tracking-tight sm:text-[8vw] lg:text-[5.4vw]">
          <HeadLine i={0} accent>Precision Repairs.</HeadLine>
          <HeadLine i={1}>Zero Waffle.</HeadLine>
          <HeadLine i={2} accent>Maximum Performance.</HeadLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.9 }}
          className="mt-9 max-w-2xl font-body text-base leading-relaxed text-neutral-300 md:text-lg"
        >
          Bristol's heavyweight independent automotive specialist bringing over{" "}
          <span className="text-white">30 years of elite, hands-on mechanical wizardry</span> to every single job.
          From complex transmission overhauls to deep electronic diagnostics — we fix what others give up on.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.25, duration: 0.9 }}
          className="mt-11 flex flex-col gap-4 sm:flex-row sm:items-center"
        >
          <button
            data-testid="hero-quote-button"
            onClick={onQuote}
            className="group flex items-center justify-center gap-3 rounded-full bg-[#ffb000] px-8 py-4 font-accent text-sm font-600 uppercase tracking-[0.2em] text-black transition-colors duration-300 hover:bg-[#ff8c00]"
          >
            Secure Your Slot / Get a Quote
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" strokeWidth={2.5} />
          </button>
          <a
            href={CONTACT.phoneHref}
            data-testid="hero-call-button"
            className="chrome-inset flex items-center justify-center gap-3 rounded-full border border-white/25 px-8 py-4 font-accent text-sm uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:bg-white/5"
          >
            <Phone className="h-4 w-4" strokeWidth={2.5} />
            Direct Line: {CONTACT.phone}
          </a>
        </motion.div>
      </div>

      {/* scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-7 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 md:flex"
      >
        <span className="font-accent text-[10px] uppercase tracking-[0.35em] text-neutral-500">Scroll</span>
        <div className="h-10 w-[1px] bg-gradient-to-b from-white/60 to-transparent" />
      </motion.div>
    </section>
  );
};
